import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, FileDown } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FadeIn, MaskedLines, Overline, Chapter } from "@/components/Motion";
import CTABand from "@/components/CTABand";
import LeadForm, { LeadDialog } from "@/components/LeadForm";
import { PRODUCTS, IMAGES } from "@/data/content";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);
  if (!product) return <Navigate to="/products" replace />;

  return (
    <>
      <section className="bg-white pt-36 sm:pt-44" data-testid="product-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="flex items-center gap-4">
              <Overline>{product.code}</Overline>
              {product.tech.map((t) => (
                <span key={t} className="font-mono text-[9px] tracking-[0.15em] uppercase text-signal border border-signal/30 rounded-full px-2 py-0.5">{t}</span>
              ))}
            </div>
          </FadeIn>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-slate-950 leading-[0.98]">
            <MaskedLines immediate lines={[product.name]} />
          </h1>
          <FadeIn delay={0.3}>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl">{product.tagline} {product.description}</p>
          </FadeIn>
          <FadeIn delay={0.4}>
            <div className="mt-8 flex flex-wrap gap-4">
              <LeadDialog
                defaultType="distributor"
                context={`Product page: ${product.name}`}
                title={`Enquire about ${product.name}`}
                trigger={
                  <button data-testid="product-cta-enquire" className="inline-flex items-center gap-2 rounded-full bg-obsidian text-white text-sm font-medium px-8 py-4 hover:bg-signal transition-colors duration-300">
                    Request Dealer Pricing <ArrowUpRight size={15} />
                  </button>
                }
              />
              <LeadDialog
                defaultType="download"
                context={`Spec sheet: ${product.name}`}
                title={`${product.name} — Technical Files`}
                trigger={
                  <button data-testid="product-cta-specsheet" className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-900 text-sm font-medium px-8 py-4 hover:border-slate-950 transition-colors duration-300">
                    <FileDown size={15} /> Spec Sheet
                  </button>
                }
              />
            </div>
          </FadeIn>
          <FadeIn delay={0.35}>
            <div className="spotlight-frame mt-12 rounded-2xl overflow-hidden border border-slate-900/10">
              <img src={product.image} alt={product.name} className="w-full h-[320px] sm:h-[520px] object-cover" data-testid="product-hero-image" />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-white py-24" data-testid="product-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="01" label="Benefits" />
          <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-slate-950">
            <MaskedLines lines={["Engineered around", "the dealer and the user"]} />
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {product.benefits.map((b, i) => (
              <FadeIn key={b.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-slate-200/80 bg-[#F8F9FA] p-7 hover:border-signal/40 transition-colors duration-300" data-testid={`benefit-${i}`}>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 font-display text-lg font-semibold tracking-tight text-slate-950">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-obsidian noise-dark py-24" data-testid="product-tech-band">
        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Overline light>{product.tech.join(" + ")} Technology</Overline>
            <h2 className="mt-6 font-display text-2xl sm:text-4xl font-medium tracking-tight text-white leading-[1.05]">
              <MaskedLines lines={product.tech.includes("WSAFE™") ? ["Engineered for", "every season."] : ["Powered by", "CDRIVE™ silence."]} />
            </h2>
            <FadeIn delay={0.15}>
              <p className="mt-5 text-base text-slate-400 leading-relaxed max-w-md">
                {product.tech.includes("WSAFE™")
                  ? "Sealed electronics, UV-stabilised materials and corrosion-proof hardware — certified for permanent outdoor duty."
                  : "A proprietary brushless drivetrain with no brushes, no gear noise and minimal maintenance — 38 dB, every ride."}
              </p>
              <Link
                to={product.tech.includes("WSAFE™") ? "/technologies/wsafe" : "/technologies/cdrive"}
                data-testid="product-tech-link"
                className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-white hover:text-signal transition-colors"
              >
                Explore the technology <ArrowRight size={15} />
              </Link>
            </FadeIn>
          </div>
          <FadeIn delay={0.2}>
            <div className="spotlight-frame rounded-2xl overflow-hidden border border-white/10">
              <img
                src={product.tech.includes("WSAFE™") ? IMAGES.wsafe : IMAGES.cdrive}
                alt={product.tech[0]}
                loading="lazy"
                className="w-full h-[340px] object-cover"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-white py-24" data-testid="product-specs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <Chapter index="02" label="Specifications" />
            <div className="rounded-2xl border border-slate-200/80 overflow-hidden">
              {product.specs.map((s, i) => (
                <div key={s.label} className={`flex items-center justify-between gap-6 px-6 py-4 ${i % 2 === 0 ? "bg-[#F8F9FA]/70" : "bg-white"}`} data-testid={`spec-${i}`}>
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">{s.label}</span>
                  <span className="text-sm font-medium text-slate-950 text-right">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Chapter index="03" label="FAQ" />
            <Accordion type="single" collapsible className="rounded-2xl border border-slate-200/80 px-6" data-testid="product-faq">
              {product.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-base font-medium text-slate-950 py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-600 leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] border-y border-slate-200/70 py-24" data-testid="product-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="04" label="Gallery" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {product.gallery.map((g, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div className="rounded-2xl overflow-hidden border border-slate-200/80 spotlight-frame">
                  <img src={g} alt={`${product.name} detail ${i + 1}`} loading="lazy" className="w-full h-64 object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24" data-testid="product-lead-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <Chapter index="05" label="Enquire" />
            <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-slate-950 leading-[1.05]">
              <MaskedLines lines={[`Add ${product.name}`, "to your range"]} />
            </h2>
            <FadeIn delay={0.15}>
              <p className="mt-5 text-base text-slate-600 leading-relaxed max-w-md">
                Distributor pricing, OEM branding options and territory availability — our business development team replies within 48 hours.
              </p>
            </FadeIn>
          </div>
          <FadeIn delay={0.2}>
            <div className="rounded-2xl border border-slate-200/80 bg-white p-7 sm:p-9">
              <LeadForm defaultType="distributor" context={`Product enquiry: ${product.name}`} />
            </div>
          </FadeIn>
        </div>
      </section>

      <CTABand />
    </>
  );
}
